import { ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { Piece, selectAllStructureNodes, selectStructure, selectStructureNodeById, setStructureSearchResult } from "../device/structureSlice"
import { AppId, AppTextFieldHandler } from "../../app/appTypes"
import { alpha, Box, Button, Checkbox, Chip, IconButton, InputAdornment, Stack, styled, TextField, Tooltip, Typography } from "@mui/material"
import { Localized } from "@fluent/react"
import { SimpleTreeView, TreeItem, treeItemClasses, TreeItemProps } from "@mui/x-tree-view"
import React from "react"
import { StructureName } from "../device/Structure"
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import Settings from '@mui/icons-material/Settings'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import { store } from "../../app/store"
import { selectRelationshipByStructureId } from "../device/structureRelationshipSlice"

const CustomTreeItem = React.forwardRef(
    (props: TreeItemProps, ref: React.Ref<HTMLLIElement>) => (
        <TreeItem {...props} ref={ref} />
    ),
)

const StyledTreeItem = styled(CustomTreeItem)(({ theme }) => ({
    [`& .${treeItemClasses.iconContainer}`]: {
        '& .close': {
            opacity: 0.3,
        },
    },
    [`& .${treeItemClasses.groupTransition}`]: {
        marginLeft: 15,
        paddingLeft: 18,
        borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
    },
    [`& .${treeItemClasses.label}:hover`]: {
        '& button': {
            visibility: 'visible',
        }
    },
    [`& .${treeItemClasses.label}`]: {
        '& button': {
            visibility: 'hidden',
        }
    },
}));

export interface MultiSelectStructureComponentProps {
    selectedIds?: AppId[]
    expandedOnOpen: boolean
    onSelectionChange: (selectedIds: AppId[]) => void
    rootId?: AppId | null
    showSelectedChips?: boolean
}

function searchFn(pattern: string): Piece[] {
    const allStructure = selectAllStructureNodes(store.getState())

    const regex = new RegExp(pattern, "i")

    let pieces: Piece[] = []

    for (let i = 0; i < allStructure.length; i++) {
        const struct = allStructure[i];

        if (struct.subStructureIds.length > 0) continue

        const result = struct.name_EN.match(regex)
        if (result && result.index !== undefined) {
            pieces.push({
                structId: struct.id,
                start: result.index,
                end: result.index + pattern.length,
                fullName: result.input ?? '',
                fragment: result[0],
            })
        }
    }

    return pieces
}

export const MultiSelectStructureComponent = ({
    selectedIds = [],
    expandedOnOpen = false,
    onSelectionChange,
    rootId = null,
    showSelectedChips = false,
}: MultiSelectStructureComponentProps) => {
    const dispatch = useAppDispatch()
    const rootStructures = useAppSelector(state =>
        rootId
            ? selectAllStructureNodes(state).filter(x => x.id === rootId)
            : selectAllStructureNodes(state).filter(x => x.parentStructureId === null))
    const allNonLeafStructureIds = useAppSelector(
        state => selectAllStructureNodes(state).filter(x => x.subStructureIds.length > 0)
            .map(({ id }) => id))
    const { search } = useAppSelector(selectStructure)

    const [checked, setChecked] = useState<Set<AppId>>(new Set(selectedIds))
    const [expanded, setExpanded] = useState<AppId[]>(expandedOnOpen === true ? allNonLeafStructureIds : [])

    useEffect(() => {
        setChecked(new Set(selectedIds))
    }, [selectedIds])

    const handleSearchChange: AppTextFieldHandler = useCallback(e => {
        let result: Piece[] | null = null
        if (e.target.value) {
            setExpanded(allNonLeafStructureIds)
            result = searchFn(e.target.value)
        }
        dispatch(setStructureSearchResult({
            pattern: e.target.value,
            result,
        }))
    }, [allNonLeafStructureIds, dispatch])

    const handleClearSearch = useCallback(() => {
        dispatch(setStructureSearchResult({
            pattern: '',
            result: null,
        }))
    }, [dispatch])

    const handleExpandAllButtonClick = useCallback(() => {
        setExpanded(allNonLeafStructureIds)
    }, [allNonLeafStructureIds])

    const handleCollapseAllButtonClick = useCallback(() => {
        setExpanded([])
    }, [])

    const handleNodeToggle = useCallback((event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpanded(nodeIds)
    }, [])

    const handleCheckboxChange = useCallback((nodeId: AppId, isChecked: boolean) => {
        setChecked(prev => {
            const next = new Set(prev)
            if (isChecked) {
                next.add(nodeId)
            } else {
                next.delete(nodeId)
            }
            onSelectionChange(Array.from(next))
            return next
        })
    }, [onSelectionChange])

    const treeRef = useRef<HTMLDivElement>(null);

    const handleRemoveSelectedStructure = useCallback((structureId: AppId) => {
        setChecked(prev => {
            const next = new Set(prev)
            next.delete(structureId)
            onSelectionChange(Array.from(next))
            return next
        })
    }, [onSelectionChange])

    const handleClearSelectedStructures = useCallback(() => {
        setChecked(new Set())
        onSelectionChange([])
    }, [onSelectionChange])

    return <>
        <Stack spacing={2} sx={{ height: '100%', maxHeight: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', overflowY: 'auto' }} ref={treeRef}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        p: 1,
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    <Button
                        onClick={handleExpandAllButtonClick}
                        startIcon={<UnfoldMoreIcon />}
                        variant='outlined'
                        size='small'
                        sx={(theme) => ({
                            pl: 0.75,
                            pr: 1.25,
                            borderRadius: 1.5,
                            fontWeight: 600,
                            borderColor: alpha(theme.palette.primary.main, 0.45),
                            color: theme.palette.primary.dark,
                            backgroundColor: alpha(theme.palette.primary.main, 0.07),
                            '& .MuiButton-startIcon': {
                                marginLeft: 0,
                                marginRight: 0.5,
                            },
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.14),
                                boxShadow: `0 1px 6px ${alpha(theme.palette.primary.main, 0.2)}`,
                            },
                        })}
                    >
                        <Localized id="structure-expand-all">
                            <span>_expand</span>
                        </Localized>
                    </Button>
                    <Button
                        onClick={handleCollapseAllButtonClick}
                        startIcon={<UnfoldLessIcon />}
                        variant='outlined'
                        size='small'
                        sx={(theme) => ({
                            pl: 0.75,
                            pr: 1.25,
                            borderRadius: 1.5,
                            fontWeight: 600,
                            borderColor: alpha(theme.palette.primary.main, 0.45),
                            color: theme.palette.primary.dark,
                            backgroundColor: alpha(theme.palette.primary.main, 0.07),
                            '& .MuiButton-startIcon': {
                                marginLeft: 0,
                                marginRight: 0.5,
                            },
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.14),
                                boxShadow: `0 1px 6px ${alpha(theme.palette.primary.main, 0.2)}`,
                            },
                        })}
                    >
                        <Localized id="structure-collapse-all">
                            <span>_collapse</span>
                        </Localized>
                    </Button>
                    <TextField sx={{
                        flexGrow: 1,
                    }}
                        value={search?.pattern ?? ''}
                        onChange={handleSearchChange}
                        InputProps={{
                            startAdornment: (<InputAdornment position='start'>
                                <SearchIcon />
                            </InputAdornment>),
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <Tooltip title={<Localized id="clear"><span>_clear</span></Localized>}>
                                        <span>
                                            <IconButton
                                                size='small'
                                                onClick={handleClearSearch}
                                                aria-label='clear search'
                                                disabled={!search?.pattern}
                                            >
                                                <CloseIcon fontSize='small' />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                        size='small'
                    />
                </Box>

                {showSelectedChips && (
                    <Box sx={{ px: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                            {selectedIds.length === 0 ? (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={<Localized id="supplier-no-structures-selected">_Brak wybranych struktur</Localized>}
                                />
                            ) : (
                                selectedIds.map((structureId) => (
                                    <Chip
                                        key={structureId}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                        onDelete={() => handleRemoveSelectedStructure(structureId)}
                                        label={<StructureName structureId={structureId} />}
                                    />
                                ))
                            )}
                            {selectedIds.length > 0 && (
                                <Button size="small" color="secondary" onClick={handleClearSelectedStructures}>
                                    <Localized id="clear"><span>_Wyczyść</span></Localized>
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}

                <SimpleTreeView
                    slots={{ collapseIcon: ArrowDropDownIcon, expandIcon: ArrowRightIcon }}
                    onExpandedItemsChange={handleNodeToggle}
                    expandedItems={expanded}
                    multiSelect={false}
                >
                    {rootStructures.map(struct =>
                        <MultiSelectStructureTreeItem
                            key={struct.id}
                            nodeId={struct.id}
                            checked={checked}
                            onCheckboxChange={handleCheckboxChange}
                        />
                    )}
                </SimpleTreeView>
            </Box>
        </Stack>
    </>;
}


interface MultiSelectStructureTreeItemProps {
    nodeId: AppId
    checked: Set<AppId>
    onCheckboxChange: (nodeId: AppId, isChecked: boolean) => void
}

const MultiSelectStructureTreeItem = ({ nodeId, checked, onCheckboxChange }: MultiSelectStructureTreeItemProps) => {
    const structure = useAppSelector(state => selectStructureNodeById(state, nodeId))
    const rel = useAppSelector(state => selectRelationshipByStructureId(state, nodeId))
    const { search } = useAppSelector(selectStructure)

    if (structure === undefined) return null

    const { subStructureIds } = structure
    const hasAnyChild = subStructureIds.length > 0

    let name: ReactNode = <StructureName structureId={structure.id} highlight={undefined} />

    if (search && search.result !== null) {
        if (hasAnyChild) {
            const hasMatchedChild = rel?.succs.some(child => search.result && search.result.findIndex(m => m.structId === child.structureId) >= 0)
            if (!hasMatchedChild) {
                return null
            }
        } else {
            const match = search.result.find(x => x.structId === structure.id)
            if (match) {
                name = <StructureName structureId={structure.id} highlight={[match.start, match.end]} />
            } else {
                return null
            }
        }
    }

    const isChecked = checked.has(nodeId)

    return <StyledTreeItem
        itemId={nodeId}
        label={
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                p: 0.5,
                pr: 0,
            }}>
                {!hasAnyChild && (
                    <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={(e) => {
                            e.stopPropagation()
                            onCheckboxChange(nodeId, e.target.checked)
                        }}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ p: 0, mr: 0.5 }}
                    />
                )}
                <Box component={hasAnyChild ? FolderOpenIcon : Settings} />
                <Typography sx={{ marginLeft: 1, marginRight: 4 }}>
                    {name}
                </Typography>
            </Box>
        }>
        {subStructureIds.map(id => <MultiSelectStructureTreeItem
            key={id}
            nodeId={id}
            checked={checked}
            onCheckboxChange={onCheckboxChange}
        />)}
    </StyledTreeItem>
}
